import { UserIntentModal } from "@/components/auth/UserIntentModal";
import { NotificationBanner } from "@/components/notification-banner";
import { useGetDashboardCommon } from "@/services";
import { currentUserAtom } from "@/store/login";
import { useAtomValue } from "jotai";
import { type PropsWithChildren, useEffect, useState } from "react";
import {
	DashboardLayoutHeader,
	DashboardLayoutMobileHeader,
	DashboardLayoutSidebar,
} from "./components";

const DRAWER_WIDTH = "242px";
const LG_BREAKPOINT = "(max-width: 1279.95px)";

/**
 * Minimal SSR-safe media-query hook that replaces MUI's useMediaQuery.
 */
function useIsLgOrLess(): boolean {
	const [matches, setMatches] = useState(() => {
		if (typeof window === "undefined") return false;
		return window.matchMedia(LG_BREAKPOINT).matches;
	});

	useEffect(() => {
		const mql = window.matchMedia(LG_BREAKPOINT);
		const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
		mql.addEventListener("change", handler);
		return () => mql.removeEventListener("change", handler);
	}, []);

	return matches;
}

const DashboardLayout = ({ children }: PropsWithChildren) => {
	const isLgOrLess = useIsLgOrLess();
	const [mobileOpen, setMobileOpen] = useState(false);

	const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

	const user = useAtomValue(currentUserAtom);

	const { data } = useGetDashboardCommon(
		{
			query: { user_id: user?.id },
		},
		{ refetchInterval: 3 * 60 * 1000 },
	);

	return (
		<div className="flex min-h-screen">
			{/* Mobile drawer */}
			{isLgOrLess ? (
				<>
					{/* Backdrop */}
					{mobileOpen && (
						// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
						<div
							className="fixed inset-0 z-40 bg-background-0/50 backdrop-blur-sm"
							onClick={handleDrawerToggle}
							aria-hidden="true"
						/>
					)}
					{/* Drawer panel */}
					<div
						className={`fixed inset-s-0 z-50 transition-all duration-300 ease-in-out ${
							mobileOpen ? "translate-x-0" : "-translate-x-full"
						}`}
						style={{
							top: 0,
							height: "100vh",
							width: DRAWER_WIDTH,
						}}
					>
						<DashboardLayoutSidebar />
					</div>
				</>
			) : (
				<DashboardLayoutSidebar />
			)}

			{/* Main content area */}
			<div className="flex flex-col flex-1 min-w-0">
				{isLgOrLess ? (
					<DashboardLayoutMobileHeader
						data={data}
						onDrawerOpen={handleDrawerToggle}
					/>
				) : null}

				<div
					className={`flex flex-col ${isLgOrLess ? "px-4" : "px-8"}`}
					style={{ marginInlineStart: isLgOrLess ? 0 : DRAWER_WIDTH }}
				>
					{isLgOrLess ? null : <DashboardLayoutHeader data={data} />}
					<NotificationBanner />
					<div className="pt-5 pb-8">{children}</div>
				</div>
			</div>

			<UserIntentModal />
		</div>
	);
};

export default DashboardLayout;
